import { Component, HostListener, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterOutlet, Router, NavigationEnd } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngrx/store";
import { filter } from "rxjs/operators";
import { AppActions } from "../../common/ngrx/app/app.reducer";
import { environment } from "../../../environments/environment";
import { AuthService } from "../../common/services/auth.service";
import { MessageService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { LayoutComponent } from "../layout/layout.component";
import { RootState } from "../../common/ngrx/root.model";
import { appDarkModeClass } from "../../app.theme";
import { DataService } from "../../common/services/data.service";
import {
  AppTheme,
  AppIdleForcedLogoutToken,
  AuthServiceNoAccessToken,
  AuthServiceDataToken,
} from "../../common/constants/session.constants";

@UntilDestroy()
@Component({
  standalone: true,
  selector: "vig-app",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  imports: [CommonModule, RouterOutlet, ToastModule, DialogModule, ButtonModule, LayoutComponent],
  providers: [MessageService],
})
export class AppComponent implements OnInit {
  constructor(
    private store: Store<RootState>,
    private authService: AuthService,
    private messageService: MessageService,
    private dataService: DataService,
    private router: Router
  ) {
    this.idleTimeoutWebWorker = new Worker(new URL("../../common/webworkers/timeout.worker", import.meta.url), {
      name: "AppComponent_timeout.worker",
      type: "module",
    });
  }

  _dialogVisible = false;
  _dialogModal = false;
  _dialogTitle: string | undefined;
  _dialogMessage: string | undefined;
  _overlayVisible = false;
  _spinnerVisible = false;

  dataInitialized = false;
  idleServiceStarted = false;
  idleInactiveDialogVisible = false;
  idleSecondsBeforeWarning = environment.idleSecondsBeforeWarning;
  idleSecondsBeforeLogout = environment.idleSecondsBeforeLogout;
  idleLoggoutCountdown = "0";
  idleTimeoutWebWorker: Worker;
  isViewerRoute = true; // Assume the viewer route is the default route to be headless initially

  @HostListener("window:keydown", ["$event"])
  handleKeyDown(): void {
    this.idleSessionKeepAlive();
  }

  @HostListener("window:mousemove", ["$event"])
  handleMouseMove(): void {
    this.idleSessionKeepAlive();
  }

  @HostListener("window:mousedown", ["$event"])
  handleMouseDown(): void {
    this.idleSessionKeepAlive();
  }

  ngOnInit(): void {
    console.log(
      `%c[AppComponent] GoVigilant.App ${environment.buildVersion} (${environment.environmentName})`,
      "color: #dae4ea; background: #218e75;"
    );

    // Update isViewerRoute based on the current URL
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.isViewerRoute = event.urlAfterRedirects.startsWith("/viewer");
    });

    this.initializeStore();
    this.checkAppTheme();
    this.checkSessionState();
  }

  initializeStore(): void {
    this.store.pipe(untilDestroyed(this)).subscribe((state: RootState) => {
      this._dialogVisible = state.app.dialogVisible;
      this._dialogModal = state.app.dialogModal;
      this._dialogTitle = state.app.dialogTitle;
      this._dialogMessage = state.app.dialogMessage;
      this._overlayVisible = state.app.overlayVisible;
      this._spinnerVisible = state.app.spinnerVisible;

      // Push any toast messages in the store then acknowledge them
      if (state.app.toasts.length > 0) {
        for (const toast of state.app.toasts) {
          this.messageService.add({
            severity: toast.severity,
            summary: toast.summary,
            detail: toast.detail,
          });
        }

        this.store.dispatch(AppActions.ClearToasts());
      }

      // Perform application bootstrapping tasks based on the user being logged in
      if (state.app.userLoggedIn) {
        if (!this.idleServiceStarted) {
          this.idleSessionStart();
        }

        if (!this.dataInitialized) {
          this.dataInitialized = true;
          this.dataService.initializeData(["self"]);
        }
      }
    });
  }

  checkAppTheme(): void {
    function setAppTheme(dark: boolean): void {
      if (dark) {
        document.querySelector("html")?.classList.add(appDarkModeClass);
      } else {
        document.querySelector("html")?.classList.remove(appDarkModeClass);
      }
    }

    const appTheme = localStorage.getItem(AppTheme);

    if (appTheme === "dark") {
      setAppTheme(true);
    } else if (appTheme === "light") {
      setAppTheme(false);
    } else {
      if (window.matchMedia) {
        setAppTheme(window.matchMedia("(prefers-color-scheme: dark)").matches);
      }
    }

    if (window.matchMedia) {
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (event) => {
        const appTheme = localStorage.getItem(AppTheme);

        if (!appTheme) {
          setAppTheme(event.matches);
        }
      });
    }
  }

  checkSessionState(): void {
    // Check if the user was logged out due to inactivity and if so display a messaging dialog
    if (sessionStorage.getItem(AppIdleForcedLogoutToken) === "true") {
      sessionStorage.removeItem(AppIdleForcedLogoutToken);

      this.store.dispatch(
        AppActions.OpenDialog({
          title: "Session Expired",
          message: "Your user session has expired, please login again to continue.",
          modal: true,
        })
      );
    }

    if (sessionStorage.getItem(AuthServiceNoAccessToken) === "true") {
      sessionStorage.removeItem(AuthServiceNoAccessToken);

      this.store.dispatch(
        AppActions.OpenDialog({
          title: "Access Denied",
          message: "The user account does not have access to the system, please contact your administrator or use another account.",
          modal: true,
        })
      );
    }

    // Check if the user is already authenticated in an active session
    const authData = sessionStorage.getItem(AuthServiceDataToken);
    if (authData) {
      this.authService
        .getUserManager()
        .getUser()
        .then((response) => {
          sessionStorage.setItem(AuthServiceDataToken, JSON.stringify(response));
          this.store.dispatch(AppActions.SetAuthState({ userLoggedIn: true, userAuthData: response }));
        });
    }
  }

  idleSessionStart(): void {
    this.idleServiceStarted = true;

    this.idleTimeoutWebWorker.postMessage({
      command: "start",
      idleSecondsBeforeWarning: this.idleSecondsBeforeWarning,
      idleSecondsBeforeLogout: this.idleSecondsBeforeLogout,
    });

    this.idleTimeoutWebWorker.onmessage = ({ data }): void => {
      if (data.command === "warning") {
        this.idleInactiveDialogVisible = true;
        this.idleLoggoutCountdown = `${data.logoutTime}`;
      } else if (data.command === "logout") {
        sessionStorage.setItem(AppIdleForcedLogoutToken, "true"); // Set session flag that the user was logged out due to inactivity
        this.idleInactiveDialogVisible = false;
        this.authService.logout();
      }
    };
  }

  idleSessionKeepAlive(): void {
    if (!this.idleInactiveDialogVisible && this.idleServiceStarted) {
      this.idleTimeoutWebWorker.postMessage({
        command: "keepalive",
      });
    }
  }

  idleAcceptInactiveDialog(): void {
    this.idleTimeoutWebWorker.postMessage({
      command: "stop",
    });
    this.idleInactiveDialogVisible = false;
    this.authService.logout();
  }

  idleDismissInactiveDialog(): void {
    this.idleTimeoutWebWorker.postMessage({
      command: "keepalive",
    });
    this.idleInactiveDialogVisible = false;
  }

  onCloseCommonDialog(): void {
    this.store.dispatch(AppActions.CloseDialog());
  }
}
